import React, { useRef } from "react";
import { useReveal, useRevealStagger } from "../hooks/useReveal";

const ABOUT_COPY = {
    pt: {
        eyebrow: "Porque somos",
        heading: "Melhores",
        paragraphs: [
            "Somos a ponte entre tecnologia e resultados reais. Usamos IA e automação para simplificar processos, enquanto mantemos a proximidade humana para garantir suporte e flexibilidade.",
        ],
        ctaLabel: "Fale connosco",
        cards: [
            {
                n: "1",
                title: "Inovação",
                desc: [
                    "Transformamos complexidade em progresso.",
                    "Desenvolvemos tecnologia que antecipa necessidades e redefine o que é possível.",
                ],
            },
            {
                n: "2",
                title: "Escala",
                desc: [
                    "Criamos soluções que crescem com os nossos clientes.",
                    "Arquiteturas desenhadas para evoluir — de forma contínua, segura e eficiente.",
                ],
            },
            {
                n: "3",
                title: "Rigor",
                desc: [
                    "Cada decisão técnica é sustentada por método e precisão.",
                    "O nosso compromisso é com a competência — da conceção à execução.",
                ],
            },
            {
                n: "4",
                title: "Qualidade",
                desc: [
                    "Controlo constante.",
                    "Melhoria contínua.",
                    "Sistemas estáveis, métricas consistentes e resultados comprovados no tempo.",
                ],
            },
        ],
    },
    en: {
        eyebrow: "Why we are",
        heading: "Better",
        paragraphs: [
            "We bridge technology and real outcomes. We use AI and automation to streamline processes while keeping a human approach to ensure support and flexibility.",
        ],
        ctaLabel: "Talk to us",
        cards: [
            {
                n: "1",
                title: "Innovation",
                desc: [
                    "We turn complexity into progress.",
                    "We develop technology that anticipates needs and redefines what’s possible.",
                ],
            },
            {
                n: "2",
                title: "Scalability",
                desc: [
                    "Solutions that grow effortlessly with your business and evolving needs.",
                    "Architectures designed to evolve — continuously, securely, and efficiently."
                ],
            },
            {
                n: "3",
                title: "Precision",
                desc: [
                    "Every technical decision is grounded in method and accuracy.",
                    "Our commitment is to excellence — from design to execution.",
                ],
            },
            {
                n: "4",
                title: "Quality",
                desc: [
                    "Constant control.",
                    "Continuous improvement.",
                    "Stable systems, consistent metrics, and proven results over time.",
                ],
            },
        ],
    },
};

export default function About({ lang = "pt" }) {

    const brand = "#4DB1A1";
    const headline = "#026E78";
    const bgSoft = "#F5F9F8";

    const copy = ABOUT_COPY[lang] || ABOUT_COPY.pt;
    const ITEMS = copy.cards;

    const headerRef = useRef(null);
    const desktopCtaRef = useRef(null);
    const cardsWrapperRef = useRef(null);
    const mobileCtaRef = useRef(null);

    useReveal(headerRef);
    useReveal(desktopCtaRef, { threshold: 0.2, rootMargin: "-10% 0px" });
    useReveal(mobileCtaRef, { threshold: 0.2 });
    useRevealStagger(cardsWrapperRef, { threshold: 0.15 });

    return (
        <section
            id="about"
            aria-labelledby="porque-somos-title"
            className="w-full scroll-mt-32 sm:scroll-mt-36 lg:scroll-mt-40"
            style={{ backgroundColor: bgSoft }}
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-14 sm:py-16 lg:py-20">
                {/* Header */}
                <div ref={headerRef} className="reveal flex items-start justify-between gap-6">
                    <div className="max-w-3xl">
                        <p className="uppercase tracking-[0.24em] text-[11px] sm:text-xs text-slate-500">
                            {copy.eyebrow}
                        </p>
                        <h2
                            id="porque-somos-title"
                            className="mt-1 text-4xl sm:text-5xl font-extrabold"
                            style={{ color: headline }}
                        >
                            {copy.heading}
                        </h2>

                        {copy.paragraphs.map((paragraph, idx) => (
                            <p key={idx} className={`text-slate-700 leading-relaxed ${idx === 0 ? "mt-4" : "mt-3"}`}>
                                {paragraph}
                            </p>
                        ))}
                    </div>

                    {/* CTA */}
                    <div ref={desktopCtaRef} className="reveal hidden sm:block shrink-0" data-reveal="fade-left">
                        <a
                            href="mailto:info@invisiblecloud.pt"
                            className="cta-hover inline-flex items-center rounded-full px-4 py-2 text-white font-semibold shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            style={{ backgroundColor: headline }}
                        >
                            {copy.ctaLabel}
                        </a>
                    </div>
                </div>

                {/* Cards */}
                <div
                    ref={cardsWrapperRef}
                    className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
                    data-reveal-delay="160"
                >
                    {ITEMS.map((it) => (
                        <article
                            key={it.n}
                            className="reveal group relative rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl focus-within:shadow-xl"
                            style={{ minHeight: 220, "--brand": headline }}
                        >
                            {/* efeito de glow no hover */}
                            <div
                                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                style={{
                                    boxShadow: `0 10px 30px -10px ${brand}22`,
                                }}
                            />

                            <div className="relative flex h-full flex-col p-6">
                                {/* número com underline animado */}
                                <div className="mt-1">
                                    <span
                                        className="relative inline-block text-3xl font-extrabold leading-none"
                                        style={{ color: headline }} // mesma cor do headline
                                    >
                                        {it.n}

                                        {/* underline */}
                                        <span
                                            aria-hidden="true"
                                            className="
        pointer-events-none absolute left-0 -bottom-1 h-[3px] w-full
        origin-left scale-x-50
        bg-[color:var(--brand)] opacity-40 rounded-full
        transition-[transform,opacity] duration-300 ease-out
        group-hover:scale-x-100 group-hover:opacity-100
        group-focus-within:scale-x-100 group-focus-within:opacity-100
      "
                                        />
                                    </span>
                                </div>

                                <h3
                                    className="mt-3 text-2xl font-extrabold tracking-tight transition-colors duration-300"
                                    style={{ color: headline }}
                                >
                                    {it.title}
                                </h3>

                                <p className="mt-3 text-slate-600 leading-relaxed">
                                    {Array.isArray(it.desc)
                                        ? it.desc.map((phrase, phraseIdx) => (
                                              <React.Fragment key={phraseIdx}>
                                                  {phrase}
                                                  {phraseIdx < it.desc.length - 1 && <br />}
                                              </React.Fragment>
                                          ))
                                        : it.desc}
                                </p>

                                {/* “shadow chip” no fundo para dar efeito de cartão físico */}
                                <div className="mt-5 rounded-2xl bg-slate-50 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>

                            {/* animações no hover: subir, aumentar borda e underline crescer */}
                            <style>{`
                article.group:hover {
                  transform: translateY(-4px);
                }
                article.group:hover .h-1 {
                  transform: scaleX(1.4);
                }
                article.group {
                  transition: transform .25s ease, box-shadow .3s ease;
                }
                .cta-hover {
                  position: relative;
                  overflow: hidden;
                  transform: translateY(0);
                  transition: transform .28s ease, box-shadow .28s ease, filter .28s ease;
                }
                .cta-hover::after {
                  content: "";
                  position: absolute;
                  inset: -10%;
                  background: linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0));
                  opacity: 0;
                  transform: translateY(45%) scale(0.95);
                  transition: opacity .35s ease, transform .35s ease;
                }
                .cta-hover:hover {
                  transform: translateY(-4px) scale(1.02);
                  box-shadow: 0 22px 35px -18px rgba(15,23,42,0.35);
                  filter: brightness(1.05);
                }
                .cta-hover:hover::after {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
                .cta-hover:focus-visible {
                  transform: translateY(-1px);
                }
              `}</style>
                        </article>
                    ))}
                </div>

                {/* CTA visível no mobile */}
                <div ref={mobileCtaRef} className="reveal sm:hidden mt-8">
                    <a
                        href="mailto:info@invisiblecloud.pt"
                        className="cta-hover inline-flex items-center rounded-full px-4 py-2 text-white font-semibold shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        style={{ backgroundColor: brand }}
                    >
                        {copy.ctaLabel}
                    </a>
                </div>
            </div>

            {/* linha base suave como no mockup */}
            <div className="h-px w-full bg-slate-200/70" />
        </section>
    );
}
